import React, { useEffect, useState } from 'react';
import { COLORS, SPACING } from '../styles/theme';
import { EventListing, getRSVPCount, getUserRSVP, publishRSVP } from '../lib/eventListings';
import { recordSignal } from '../lib/signals';
import { asText, parseMagnetURI } from '../lib/sanitize';
import { loadOrCreateIdentity } from '../lib/identity';
import { getFile } from '../lib/fileStorage';
import { getProfile, getRemoteProfileByPubkey, calculateTastemakerScore } from '../lib/metadata';

interface Props {
  listing: EventListing;
  onClick?: (listing: EventListing) => void;
  onReload?: () => void;
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function EventCard({ listing, onClick, onReload }: Props) {
  const identity = loadOrCreateIdentity();
  const counts = getRSVPCount(listing.id);
  const myRSVP = getUserRSVP(listing.id, identity.userId);
  const organizerProfile = listing.pubkey === identity.publicKey ? getProfile() : getRemoteProfileByPubkey(listing.pubkey);
  const organizerScore = organizerProfile ? calculateTastemakerScore(organizerProfile.userId) : 0;
  const tags = listing.tags ?? [];
  const [shared, setShared] = useState(false);
  const [flyerURL, setFlyerURL] = useState<string | null>(null);

  useEffect(() => {
    if (!listing.flyerMagnetURI || listing.flyerType !== 'image') return;
    const parsed = parseMagnetURI(listing.flyerMagnetURI);
    if (!parsed) return;
    getFile(parsed.infoHash).then(blob => {
      if (blob) setFlyerURL(URL.createObjectURL(blob));
    }).catch(() => {});
    return () => { if (flyerURL) URL.revokeObjectURL(flyerURL); };
  }, [listing.flyerMagnetURI]); // eslint-disable-line

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const handleRSVP = async (e: React.MouseEvent, status: 'going' | 'interested') => {
    stop(e);
    await publishRSVP(listing.id, status);
    recordSignal(listing.id, status === 'going' ? 'pin' : 'save', listing.flyerMagnetURI);
    onReload?.();
  };

  const handleShare = (e: React.MouseEvent) => {
    stop(e);
    recordSignal(listing.id, 'share', listing.flyerMagnetURI);
    navigator.clipboard.writeText(`auth:event:${listing.id}`).catch(() => {});
    setShared(true);
    setTimeout(() => setShared(false), 1500);
  };

  return (
    <div onClick={() => onClick?.(listing)} style={{ padding: SPACING.md, borderBottom: `1px solid ${COLORS.border}`, cursor: onClick ? 'pointer' : 'default' }}>
      {flyerURL && (
        <img src={flyerURL} alt="" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block', marginBottom: SPACING.sm }} />
      )}
      <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '16px', fontWeight: 700, color: COLORS.text, marginBottom: SPACING.xs }}>
        {asText(listing.title)}
      </div>
      <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px', color: COLORS.highlight, marginBottom: SPACING.xs }}>
        {formatDate(listing.startTime)} · {formatTime(listing.startTime)}
        {listing.endTime ? ` → ${formatTime(listing.endTime)}` : ''}
      </div>
      <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '11px', color: COLORS.textMuted, marginBottom: SPACING.sm, display: 'flex', gap: SPACING.sm, alignItems: 'center' }}>
        <span>{listing.venue ? `${asText(listing.venue)} · ` : ''}{asText(listing.city)}</span>
        {organizerScore > 0 && <span style={{ color: COLORS.accent, fontSize: '10px' }}>★ {organizerScore}</span>}
      </div>
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: SPACING.xs, flexWrap: 'wrap', marginBottom: SPACING.sm }}>
          {tags.slice(0, 5).map(tag => (
            <span key={tag} style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px', color: COLORS.textMuted, border: `1px solid ${COLORS.border}`, padding: `0 ${SPACING.xs}` }}>
              {tag}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: SPACING.lg, fontSize: '11px', alignItems: 'center' }} onClick={stop}>
        <button onClick={e => handleRSVP(e, 'going')} style={{ background: myRSVP === 'going' ? COLORS.accent : 'none', border: `1px solid ${myRSVP === 'going' ? COLORS.accent : COLORS.border}`, color: myRSVP === 'going' ? '#fff' : COLORS.text, padding: `${SPACING.xs} ${SPACING.sm}`, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', fontSize: '11px' }}>
          ✓ going ({counts.going})
        </button>
        <button onClick={e => handleRSVP(e, 'interested')} style={{ background: myRSVP === 'interested' ? COLORS.panel : 'none', border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, padding: `${SPACING.xs} ${SPACING.sm}`, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', fontSize: '11px' }}>
          ? interested ({counts.interested})
        </button>
        <button onClick={handleShare} style={{ background: 'none', border: 'none', color: shared ? COLORS.highlight : COLORS.textMuted, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', padding: 0 }}>
          {shared ? '✓ copied' : '⇥ share'}
        </button>
      </div>
    </div>
  );
}
